import * as React from 'react';
import { Context as ReactContext } from 'react';
import type { IEmployeeExperienceContext } from '@micro-frontend-react/employee-experience/lib/IEmployeeExperienceContext';
import type { IUser } from '@micro-frontend-react/employee-experience/lib/IUser';
import { Context } from '@micro-frontend-react/core/lib/Context';

import type { IPBTCase } from '../../../Interfaces/IPBTCase';
import { PBTEngagementModel } from '../../../Interfaces/IPBTCase';
import { PBTApiEndpoints } from '../../../Services/PBTConstants';
import { AttestationPanelMessages as msg } from './AttestationPanel.messages';
import { SEVEN_DAY_STATES } from './AttestationPanel.constants';

export type AttestationMode = 'none' | 'manager' | 'hrc';
type StaticAttestActionKey = 'override' | 'managerHRCLed';

export interface IAttestActionConfig {
  key: 'manager' | 'onBehalf' | 'override' | 'managerHRCLed';
  text: string;
  hint: string;
  iconName: string;
  mode: Exclude<AttestationMode, 'none'>;
}

interface IUseAttestationPanelOptions {
  attestFeatureEnabled: boolean;
  attestOnBehalfFeatureEnabled: boolean;
  canAttestOverride: boolean;
  role?: string | null;
}

function getManagerAction(isManagerLed: boolean): IAttestActionConfig {
  return {
    key: 'manager',
    text: isManagerLed ? msg.attestAsManagerBtn : msg.completeAttestationBtn,
    hint: isManagerLed ? msg.attestAsManagerHint : msg.completeAttestationHint,
    iconName: 'EditContact',
    mode: 'manager',
  };
}

function getPresetAttestAction(actionKey: StaticAttestActionKey): IAttestActionConfig {
  const presetAttestActions: Record<StaticAttestActionKey, IAttestActionConfig> = {
    override: {
      key: 'override',
      text: msg.attestOnBehalfBtn,
      hint: msg.adminAttestationHint,
      iconName: 'Admin',
      mode: 'hrc',
    },
    managerHRCLed: {
      key: 'managerHRCLed',
      text: msg.attestOnBehalfBtn,
      hint: msg.attestOnBehalfHint,
      iconName: 'PeopleRepeat',
      mode: 'hrc',
    },
  };

  return presetAttestActions[actionKey];
}

function getAttestActionByRules(
  selectedCase: IPBTCase,
  userAlias: string | null,
  canAttestOverride: boolean,
  role: string | null,
): IAttestActionConfig | null {
  if (!userAlias) {
    return canAttestOverride ? getPresetAttestAction('override') : null;
  }

  const alias = userAlias.toLowerCase();
  const isManager = selectedCase.managerAlias?.toLowerCase() === alias;
  const isHRC = selectedCase.hrc?.toLowerCase() === alias;
  const engModel = selectedCase.engModel?.trim();

  const isManagerLed = engModel === PBTEngagementModel.ManagerLed.trim();
  const isHRCLed = engModel === PBTEngagementModel.HRCLed.trim();
  const isManagerHRCLed = engModel === PBTEngagementModel.ManagerHRCLed.trim();

  if ((isManagerLed && isManager) || (isHRCLed && isHRC)) {
    return getManagerAction(isManagerLed);
  }

  if (isManagerHRCLed && (isHRC || isManager)) {
    // Label follows the logged-in role, not just the engagement model:
    // a Manager attests "as Manager"; HRC/HRCLeader/Admin attest "on Behalf of Manager".
    return role === 'Manager'
      ? getManagerAction(true)
      : getPresetAttestAction('managerHRCLed');
  }

  // The assigned HRC may attest their OWN case for ANY engagement model — including a Manager-led
  // case where the manager has not acted. They attest on the manager's behalf. The backend already
  // authorizes the assigned HRC regardless of engagement model, so surface the action here.
  if (isHRC) {
    return getPresetAttestAction('managerHRCLed');
  }

  return canAttestOverride ? getPresetAttestAction('override') : null;
}

export interface IAttestationState {
  isChecked: boolean;
  isModalOpen: boolean;
  isSubmitting: boolean;
  isSubmitted: boolean;
  validationError: string | null;
  submitError: string | null;
  effectiveTermDate: string | null;
  lossOfAccessDate: string | null;
}

const INITIAL_STATE: IAttestationState = {
  isChecked: false,
  isModalOpen: false,
  isSubmitting: false,
  isSubmitted: false,
  validationError: null,
  submitError: null,
  effectiveTermDate: null,
  lossOfAccessDate: null,
};


export function useAttestationPanel(
  selectedCase: IPBTCase | null,
  attestMode: AttestationMode = 'none',
  options?: IUseAttestationPanelOptions,
) {
  const { authClient } = React.useContext(Context as ReactContext<IEmployeeExperienceContext>);
  const [loggedInUser, setLoggedInUser] = React.useState<IUser | null>(null);

  React.useEffect(() => {
    authClient.getUser().then(setLoggedInUser);
  }, [authClient]);

  const userAlias = loggedInUser?.email?.split('@')[0]?.toLowerCase() || '';

  const [state, setState] = React.useState<IAttestationState>(INITIAL_STATE);
  const previousSelectedCaseId = React.useRef<number | null>(selectedCase?.id ?? null);

  // Reset state when selected case changes
  React.useEffect(() => {
    const nextSelectedCaseId = selectedCase?.id ?? null;
    const lastSelectedCaseId = previousSelectedCaseId.current;

    previousSelectedCaseId.current = nextSelectedCaseId;

    if (lastSelectedCaseId !== null && nextSelectedCaseId !== null && lastSelectedCaseId !== nextSelectedCaseId) {
      setState(INITIAL_STATE);
    }
  }, [selectedCase?.id]);

  // Reset state when dialog is closed
  React.useEffect(() => {
    if (attestMode === 'none') {
      setState((prev) => (prev.isSubmitted ? prev : INITIAL_STATE));
    }
  }, [attestMode]);

  const setChecked = React.useCallback((checked: boolean) => {
    setState((prev) => ({
      ...prev,
      isChecked: checked,
      validationError: checked ? null : prev.validationError,
    }));
  }, []);

  const handleSubmitClick = React.useCallback(() => {
    if (!state.isChecked) {
      setState((prev) => ({ ...prev, validationError: msg.validationMessage }));
      return;
    }
    // Open confirmation modal
    setState((prev) => ({ ...prev, isModalOpen: true, validationError: null }));
  }, [state.isChecked]);

  const handleModalDismiss = React.useCallback(() => {
    setState((prev) => ({ ...prev, isModalOpen: false }));
  }, []);

  const handleConfirm = React.useCallback(async () => {
    if (!selectedCase) return;

    setState((prev) => ({ ...prev, isModalOpen: false, isSubmitting: true, submitError: null }));

    try {
      const token = await authClient.acquireToken(__SCOPES__);
      const response = await fetch(PBTApiEndpoints.TermConversation(selectedCase.id), {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userAlias: userAlias,
        }),
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Case not found. It may have been removed or the ID is invalid.');
        }
        throw new Error('Something went wrong while confirming the attestation. Please try again.');
      }

      const result = await response.json();
      const data = result?.data ?? result;

      setState((prev) => ({
        ...prev,
        isSubmitting: false,
        isSubmitted: true,
        effectiveTermDate: data?.effectiveTerminationDate || null,
        lossOfAccessDate: data?.lossOfAccessDate || null,
      }));
    } catch (err) {
      setState((prev) => ({
        ...prev,
        isSubmitting: false,
        submitError:
          err instanceof Error ? err.message : 'Something went wrong while updating the attestation status.',
      }));
    }
  }, [selectedCase, authClient, userAlias, loggedInUser]);

  const is7DayState = React.useMemo(() => {
    if (!selectedCase?.workState) return false;
    return SEVEN_DAY_STATES.includes(selectedCase.workState.toUpperCase());
  }, [selectedCase?.workState]);

  const attestActions: IAttestActionConfig[] = React.useMemo(() => {
    if (!selectedCase) return [];
    if (!options?.attestFeatureEnabled) return [];

    const canAttestOnBehalf = !!options.attestOnBehalfFeatureEnabled;

    const action = getAttestActionByRules(
      selectedCase,
      userAlias,
      !!options.canAttestOverride && canAttestOnBehalf,
      options.role ?? null,
    );

    if (action?.key === 'onBehalf' && !canAttestOnBehalf) {
      return [];
    }

    return action ? [action] : [];
  }, [
    selectedCase,
    userAlias,
    options?.attestFeatureEnabled,
    options?.canAttestOverride,
    options?.attestOnBehalfFeatureEnabled,
    options?.role,
  ]);

  return {
    ...state,
    is7DayState,
    attestActions,
    userAlias,
    setChecked,
    handleSubmitClick,
    handleModalDismiss,
    handleConfirm,
  };
}